import { create } from 'zustand';
import { persist } from 'zustand/middleware';
// @ts-ignore - goServerAPI is a JavaScript module
import { createSalesSDK } from '../../goServerAPI';
import { createThrottledJSONStorage } from './throttledStorage';

// One-time cleanup: drop the legacy bloated key so it stops occupying
// localStorage budget. Safe no-op when not present.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('go-server-store-v3');
  } catch {
    // ignore quota / access errors
  }
}

import {
  ConnectionStatus,
  Device,
  SalesCallStatus,
  TranscriptionStatus,
  SetupStatus,
  MetaStatus,
  InitializationStatus,
  PeriodicCheckStatus
} from './modules/types';

import { createConnectionModule } from './modules/connection';
import { createDevicesModule } from './modules/devices';
import { createSalesModule } from './modules/sales';
import { createTranscriptionModule } from './modules/transcription';
import { createSetupModule } from './modules/setup';
import { createTranscriptionSettingsModule, TranscriptionSettingsState, TranscriptionSettingsActions } from './modules/transcriptionSettings';
import { startAutoDisconnectTimer } from './modules/sales/autoDisconnect';

export interface GoServerState {
  // Connection status
  connectionStatus: ConnectionStatus;

  // Devices
  devices: Device[];
  devicesLoading: boolean;
  devicesError: string | null;

  // Sales
  salesCallStatus: SalesCallStatus;
  salesLoading: boolean;
  salesError: string | null;
  urgentHelpInFlight?: boolean;
  checklistQuestionInFlight?: string | null;
  digDeeperInFlight?: boolean;
  objectionInFlight?: boolean;
  isProcessingSummary?: boolean;
  tourMode: boolean;
  tourDemoReady: boolean;
  tourBusinessInfo: string | null;
  tourActiveStepId: string | null;
  dashboardTourActive: boolean;
  spotlightTourActive: boolean;

  // Transcription
  transcriptionStatus: TranscriptionStatus;
  transcriptionLoading: boolean;
  transcriptionError: string | null;

  // Transcription Settings
  transcriptionSettings: TranscriptionSettingsState & TranscriptionSettingsActions;

  // Setup
  setupStatus: SetupStatus;
  setupLoading: boolean;
  setupError: string | null;

  // Meta
  metaStatus: MetaStatus;
  metaLoading: boolean;
  metaError: string | null;

  // Initialization
  initializationStatus: InitializationStatus;

  // Periodic Check
  periodicCheckStatus: PeriodicCheckStatus;

  // SDK instance
  sdk: ReturnType<typeof createSalesSDK> | null;

  // Actions
  initializeSDK: () => void;
  checkConnection: () => Promise<void>;
  fetchDevices: (opts?: { force?: boolean; hard?: boolean }) => Promise<void>;
  hardRefreshDevices: () => Promise<void>;
  testDevice: (deviceId: string) => Promise<void>;
  startSalesCall: (salesmanMicId: string, clientMicId: string, userId?: string | null, centerType?: string) => Promise<void>;
  setCallMode: (mode: 'real' | 'simulator') => void;
  setSimulatorDifficulty: (difficulty: 'easy' | 'medium' | 'hard') => void;
  setSimulatorVoice: (voice: 'Eve' | 'Ara' | 'Rex' | 'Sal' | 'Leo') => void;
  checkActiveCall: () => Promise<void>; // Exposed for manual re-sync
  stopSalesCall: () => Promise<void>;
  pauseSalesCall: () => Promise<void>;
  resumeSalesCall: () => Promise<void>;
  requireImmediateHelp: () => Promise<void>;
  setTourMode: (active: boolean, businessInfo?: string) => void;
  setTourActiveStep: (stepId: string | null) => void;
  setTourDemoReady: (ready: boolean) => void;
  setDashboardTourActive: (active: boolean) => void;
  setSpotlightTourActive: (active: boolean) => void;
  clearWhatToSayData: () => void;
  runTourAgent: (agentName: string, questionText: string) => Promise<void>;
  askChecklistQuestion: (questionId: string, questionText: string) => Promise<void>;
  digDeeper: (questionId: string, questionText: string) => Promise<void>;
  handleObjection: (objectionName: string, objectionSolution: string, currentSummary?: any) => Promise<void>;
  sendFullTranscriptToAgent: (summaryOverride?: string) => Promise<void>;
  generateWhatsAppMessages: (summary?: string) => Promise<any>;
  updateSummarizerSummary: (summary: string) => void;
  updateSummarizerAmmo: (ammoItems: { id: string, text: string, timestamp?: string }[]) => void;
  setFeedbackStreaming: (isStreaming: boolean) => void;
  setFeedbackResult: (result: any) => void;
  setSummaryStreaming: (isStreaming: boolean) => void;
  setSummaryResult: (result: any) => void;
  setWhatsappStreaming: (isStreaming: boolean) => void;
  setWhatsappResult: (result: any) => void;
  setAnalysisStreaming: (isStreaming: boolean) => void;
  setAnalysisResult: (result: any) => void;
  setAnalysisRequested: (requested: boolean) => void;
  connectTranscription: (deviceId: string) => void;
  disconnectTranscription: (deviceId: string) => void;
  clearTranscriptionHistory: () => void;
  resetConversationData: (preserveFeedback?: boolean) => void;
  checkSetupStatus: () => Promise<void>;
  installLocalCAR: () => Promise<void>;
  fetchMetaVersion: () => Promise<void>;
  resetErrors: () => void;
  initializeAndFetchDevices: () => Promise<void>;
  startPeriodicConnectionCheck: () => () => void;
}

export const useGoServerStore = create<GoServerState>()(persist((set, get) => ({
  // SDK instance
  sdk: null,

  // Initialize SDK action
  initializeSDK: () => {
    const sdk = createSalesSDK();
    set({ sdk });
  },

  // Combine all modules
  ...createConnectionModule(set, get),
  ...createDevicesModule(set, get),
  ...createSalesModule(set, get),
  ...createTranscriptionModule(set, get),
  ...createSetupModule(set, get),
  transcriptionSettings: createTranscriptionSettingsModule(
    (partial) => set((state) => ({ ...state, transcriptionSettings: { ...state.transcriptionSettings, ...partial } })),
    () => get().transcriptionSettings,
    () => get().sdk
  ),
}), {
  name: 'go-server-store-v4',
  storage: createThrottledJSONStorage<Partial<GoServerState>>(),
  // Persist only the small identity fields needed to reattach to an active call.
  // Ephemeral / fast-growing data (transcriptionHistory, liveTranscriptions,
  // whatToResponseHistory, accumulatedSummary.*) is excluded — it's recreated
  // by the agent/transcription streams, and onRehydrateStorage wipes
  // conversation data when no active call is detected.
  partialize: (state) => ({
    salesCallStatus: {
      isActive: state.salesCallStatus.isActive,
      isPaused: state.salesCallStatus.isPaused,
      pauseStartedAt: state.salesCallStatus.pauseStartedAt,
      accumulatedPauseMs: state.salesCallStatus.accumulatedPauseMs,
      callMode: state.salesCallStatus.callMode,
      simulatorDifficulty: state.salesCallStatus.simulatorDifficulty,
      simulatorVoice: state.salesCallStatus.simulatorVoice,
      simulatorStatus: state.salesCallStatus.simulatorStatus,
      callId: state.salesCallStatus.callId,
      salesmanMicId: state.salesCallStatus.salesmanMicId,
      clientMicId: state.salesCallStatus.clientMicId,
      startTime: state.salesCallStatus.startTime,
    },
    transcriptionSettings: state.transcriptionSettings,
  }) as unknown as GoServerState,
  merge: (persistedState, currentState) => {
    // Deep-merge so missing nested keys (e.g. accumulatedSummary, agentData)
    // fall back to the freshly initialized defaults from the module factories.
    const ps = (persistedState ?? {}) as Partial<GoServerState>;
    return {
      ...currentState,
      ...ps,
      salesCallStatus: {
        ...currentState.salesCallStatus,
        ...(ps.salesCallStatus ?? {}),
      },
      transcriptionSettings: {
        ...currentState.transcriptionSettings,
        ...(ps.transcriptionSettings ?? {}),
      },
    };
  },
  onRehydrateStorage: () => (state) => {
    if (!state) return;
    // Use a timeout so the store is fully attached before getState / timers run.
    setTimeout(() => {
      if (!state.salesCallStatus.isActive) {
        console.log('[STORE] No active call detected on rehydrate, resetting conversation data (preserving feedback)');
        state.resetConversationData(true);
        return;
      }
      // Persisted sessions lose JS timers on reload; re-arm wall-clock auto-disconnect.
      const st = useGoServerStore.getState().salesCallStatus.startTime;
      startAutoDisconnectTimer(
        () => useGoServerStore.getState().salesCallStatus?.isActive,
        () => {
          void useGoServerStore.getState().stopSalesCall();
        },
        st ?? undefined
      );
    }, 0);
  },
}));

// Re-export types for convenience
export * from './modules/types';
export * from './modules/transcriptionSettings';
